'use client';

import React, { useEffect, useState } from 'react';
import Image from 'next/image';
import { useLocale } from 'next-intl';
import { studioClient } from '@/sanity/lib/client';
import { localizedObjectiveCardsQuery } from '@/sanity/queries/objectiveCards';
import { IObjectiveCards, ILocalizedObjectiveCard } from '@/types/objectiveCards';
import { LoadingSpinner } from '@/shared';
import objectiveEllipse from '@/assets/illustrations/objective-ellipse.svg';

interface ObjectiveCardProps {
  card: ILocalizedObjectiveCard;
}

const BackgroundBlur = ({
  position,
}: {
  position:
    | 'top-left'
    | 'top-right'
    | 'bottom-left'
    | 'bottom-right'
    | 'center-left'
    | 'center-right'
    | 'center';
}) => {
  const getPositionStyles = () => {
    switch (position) {
      case 'top-left':
        return { left: '-20%', top: '0%' };
      case 'top-right':
        return { right: '-20%', top: '0%' };
      case 'bottom-left':
        return { left: '-20%', bottom: '0%' };
      case 'bottom-right':
        return { right: '-20%', bottom: '0%' };
      case 'center-left':
        return { left: '-20%', top: '50%', transform: 'translateY(-50%)' };
      case 'center-right':
        return { right: '-20%', top: '50%', transform: 'translateY(-50%)' };
      case 'center':
        return { left: '50%', top: '50%', transform: 'translate(-50%, -50%)' };
      default:
        return { left: '-20%', top: '0%' };
    }
  };

  return (
    <div
      className="hidden lg:block"
      style={{
        position: 'absolute',
        width: '600px',
        height: '600px',
        borderRadius: '50%',
        opacity: 0.2,
        background: 'rgba(32, 255, 36, 0.60)',
        filter: 'blur(161.04000854492188px)',
        zIndex: 0,
        pointerEvents: 'none',
        ...getPositionStyles(),
      }}
      aria-hidden="true"
    />
  );
};

const ObjectiveCard: React.FC<ObjectiveCardProps> = ({ card }) => {
  const getCardPosition = (position: string) => {
    switch (position) {
      case 'top-left':
        return 'col-start-1 row-start-1 sm:col-start-1 sm:row-start-1';
      case 'top-right':
        return 'col-start-1 row-start-2 sm:col-start-2 sm:row-start-1';
      case 'middle-left':
        return 'col-start-1 row-start-3 sm:col-start-1 sm:row-start-2';
      case 'middle-right':
        return 'col-start-1 row-start-4 sm:col-start-2 sm:row-start-2';
      case 'bottom-center':
        return 'col-start-1 sm:w-[50%] w-full row-start-5 sm:col-start-1 sm:col-span-2 sm:row-start-3 sm:justify-self-center';
      default:
        return 'col-start-1 row-start-1';
    }
  };

  return (
    <div
      className={`relative rounded-2xl border border-white/20 bg-gradient-to-r from-white/10 to-white/0 p-8 backdrop-blur-sm ${getCardPosition(card.position)}`}
      style={{
        background:
          'linear-gradient(90deg, rgba(255, 255, 255, 0.10) 0%, rgba(255, 255, 255, 0.00) 100%)',
      }}>
      {/* Green left border */}
      <div
        className="absolute top-16 h-12 w-1 ltr:left-0 ltr:rounded-r-lg rtl:right-0 rtl:rounded-l-lg"
        style={{
          background: '#46E72D',
          boxShadow: '0 0 20px 5px rgba(74, 231, 59, 0.40)',
        }}
      />

      {/* Icon */}
      <div className="flex justify-start">
        {/* Ellipse with sant icon Image */}
        <div className="flex justify-center">
          <div className="relative h-[140px] w-[140px]">
            <Image
              src={objectiveEllipse}
              alt="objective-ellipse"
              className="absolute h-[140px] w-[140px]"
            />
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="relative h-8 w-8">
                <Image
                  src={card.icon.asset.url}
                  alt={card.icon.alt || card.title}
                  fill
                  className="object-contain"
                  sizes="(max-width: 32) 32px, 32px"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="text-start">
        <h3 className="mb-2 text-3xl font-bold text-[#EDEDF2]">{card.title}</h3>
        <p className="text-xl leading-relaxed text-[#EDEDF2B2]">{card.description}</p>
      </div>
    </div>
  );
};

export default function Objective() {
  const locale = useLocale();
  const [objectiveData, setObjectiveData] = useState<IObjectiveCards | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchObjectiveData = async () => {
      try {
        const data = await studioClient.fetch<IObjectiveCards>(
          localizedObjectiveCardsQuery(locale)
        );
        setObjectiveData(data);
      } catch (error) {
        console.error('Error fetching objective data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchObjectiveData();
  }, [locale]);

  if (loading) {
    return (
      <section className="py-16">
        <div className="flex justify-center">
          <LoadingSpinner size="lg" />
        </div>
      </section>
    );
  }

  if (!objectiveData) {
    return null;
  }

  // Get localized content
  const localizedTitle =
    objectiveData.title[locale as keyof typeof objectiveData.title] || objectiveData.title.en;

  const localizedCards: ILocalizedObjectiveCard[] = objectiveData.cards.map((card) => ({
    ...card,
    title: card.title[locale as keyof typeof card.title] || card.title.en,
    description: card.description[locale as keyof typeof card.description] || card.description.en,
  }));

  return (
    <section className="relative py-16">
      {/* Background Blur Effects */}
      <BackgroundBlur position="center-left" />
      <BackgroundBlur position="center-right" />
      <BackgroundBlur position="center" />
      <div className="mx-auto mt-12 max-w-7xl px-4">
        {/* Section Header */}
        <div className="mb-12 text-center">
          <h2 className="text-hero-gradient-text text-2xl leading-tight font-bold sm:text-3xl md:text-4xl lg:text-5xl">
            {localizedTitle}
          </h2>
        </div>

        {/* Cards Grid */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-6 lg:gap-8">
          {localizedCards.map((card, index) => (
            <ObjectiveCard key={`${card.position}-${index}`} card={card} />
          ))}
        </div>
      </div>
    </section>
  );
}
